import React, { useState } from 'react';
import { ATTRIBUTES, SEGMENTS, SEGMENT_NAMES } from '../../scoring';

const styles = {
    container: { marginTop: '20px' },
    roundSection: { marginBottom: '30px' },
    roundTitle: {
        fontSize: '18px', fontWeight: 'bold', color: '#4b2e83',
        marginBottom: '10px', borderBottom: '2px solid #4b2e83', paddingBottom: '4px',
    },
    table: {
        width: '100%', borderCollapse: 'collapse', fontSize: '13px',
    },
    th: {
        backgroundColor: '#4b2e83', color: '#fff', padding: '8px 6px',
        textAlign: 'center', borderBottom: '2px solid #ddd', whiteSpace: 'nowrap',
    },
    thLeft: {
        backgroundColor: '#4b2e83', color: '#fff', padding: '8px 6px',
        textAlign: 'left', borderBottom: '2px solid #ddd',
    },
    td: {
        padding: '6px', textAlign: 'center', borderBottom: '1px solid #eee',
    },
    tdLeft: {
        padding: '6px', textAlign: 'left', borderBottom: '1px solid #eee', fontWeight: 'bold',
    },
    noData: {
        textAlign: 'center', color: '#999', padding: '20px', fontStyle: 'italic',
    },
    overlayRow: {
        backgroundColor: '#f0ebf8',
    },
    overlayTd: {
        padding: '4px 6px', textAlign: 'center', borderBottom: '1px solid #d9d2e9',
        fontSize: '12px', color: '#666',
    },
    overlayLabel: {
        padding: '4px 6px', textAlign: 'left', borderBottom: '1px solid #d9d2e9',
        fontSize: '12px', color: '#4b2e83', fontStyle: 'italic',
    },
    toggleRow: {
        display: 'flex', justifyContent: 'center', gap: '10px', marginBottom: '20px',
    },
    segmentSelect: {
        padding: '8px 16px', backgroundColor: '#e0e0e0', color: '#333',
        border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold',
        fontSize: '13px',
    },
    segmentSelectActive: {
        padding: '8px 16px', backgroundColor: '#4b2e83', color: '#fff',
        border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold',
        fontSize: '13px',
    },
};

// Color-code preference values: 4-5 = high (green), 3 = medium (amber), 1-2 = low (gray)
const prefColor = (val) => {
    if (val >= 4) return { color: '#2e7d32', fontWeight: 'bold' };
    if (val === 3) return { color: '#e65100' };
    return { color: '#999' };
};

const ResearchReport = ({ scores, teams, rounds: roundsProp }) => {
    const teamNames = teams.map(t => t.teamName).sort();
    const rounds = roundsProp || [1, 2, 3];
    const [selectedSegment, setSelectedSegment] = useState(null);

    return (
        <div style={styles.container}>
            {/* Segment overlay selector */}
            <div style={{ marginBottom: '16px' }}>
                <div style={{ textAlign: 'center', fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                    Show segment preferences:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '6px' }}>
                    <button
                        style={selectedSegment === null ? styles.segmentSelectActive : styles.segmentSelect}
                        onClick={() => setSelectedSegment(null)}
                    >
                        None
                    </button>
                    {SEGMENT_NAMES.map(seg => (
                        <button
                            key={seg}
                            style={selectedSegment === seg ? styles.segmentSelectActive : styles.segmentSelect}
                            onClick={() => setSelectedSegment(seg)}
                        >
                            {seg}
                        </button>
                    ))}
                </div>
            </div>

            {rounds.map(round => {
                const roundScores = scores[`round${round}`];
                return (
                    <div key={round} style={styles.roundSection}>
                        <div style={styles.roundTitle}>Round {round}</div>
                        {!roundScores ? (
                            <p style={styles.noData}>No data for Round {round}</p>
                        ) : (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr>
                                            <th style={styles.thLeft}></th>
                                            {ATTRIBUTES.map(attr => (
                                                <th key={attr} style={styles.th}>{attr}</th>
                                            ))}
                                            <th style={styles.th}>Price</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* Segment preference row (if selected) */}
                                        {selectedSegment && (
                                            <tr style={styles.overlayRow}>
                                                <td style={styles.overlayLabel}>
                                                    {selectedSegment} prefs
                                                </td>
                                                {ATTRIBUTES.map(attr => {
                                                    const pref = SEGMENTS[selectedSegment]?.preferences?.[attr] ?? '-';
                                                    return (
                                                        <td key={attr} style={{ ...styles.overlayTd, ...prefColor(pref) }}>
                                                            {pref}
                                                        </td>
                                                    );
                                                })}
                                                <td style={styles.overlayTd}>
                                                    <span style={{ fontSize: '11px', color: '#999' }}>
                                                        Pop: {(SEGMENTS[selectedSegment]?.population || 0).toLocaleString()}
                                                    </span>
                                                </td>
                                            </tr>
                                        )}
                                        {teamNames.map(name => {
                                            const teamResult = roundScores[name];
                                            const bike = teamResult?.bike || {};
                                            const teamRaw = teams.find(t => t.teamName === name);
                                            const price = parseFloat(
                                                teamRaw?.teamData?.rounds?.[round - 1]?.profit_margin?.price_per_bike_dollars
                                            ) || 0;
                                            return (
                                                <tr key={name}>
                                                    <td style={styles.tdLeft}>{name}</td>
                                                    {ATTRIBUTES.map(attr => {
                                                        const val = bike[attr];
                                                        let cellStyle = styles.td;
                                                        // Highlight match/mismatch if segment selected
                                                        if (selectedSegment && val != null) {
                                                            const pref = SEGMENTS[selectedSegment]?.preferences?.[attr] || 0;
                                                            if (pref >= 4 && val >= 4) {
                                                                cellStyle = { ...styles.td, backgroundColor: '#e8f5e9' };
                                                            } else if (pref >= 4 && val <= 2) {
                                                                cellStyle = { ...styles.td, backgroundColor: '#ffebee' };
                                                            }
                                                        }
                                                        return (
                                                            <td key={attr} style={cellStyle}>{val ?? '-'}</td>
                                                        );
                                                    })}
                                                    <td style={styles.td}>${price.toFixed(2)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default ResearchReport;
